import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectorIcon } from '@heroicons/react/solid'
import Switch from 'components/atoms/Switch'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { trim, toLower, includes, find, filter, some } from 'lodash'
import countries from 'pages/company/modals/countries'

const normalize = value => toLower(trim(String(value || '')))

const CountryCombobox = ({ id, value, onChange, options, placeholder, dir }) => {
  const [open, setOpen] = useState(false)
  const [preferFullList, setPreferFullList] = useState(false)
  const containerRef = useRef(null)

  const filteredOptions = useMemo(() => {
    const query = normalize(value)
    if (!query) return options
    return filter(options, option => includes(normalize(option.name), query))
  }, [options, value])

  const listOptions = preferFullList ? options : filteredOptions

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
        setPreferFullList(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isRtl = dir === 'rtl'

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          type="text"
          id={id}
          value={value}
          dir={dir}
          onChange={e => {
            const nextValue = e.target.value
            onChange(nextValue)
            setPreferFullList(false)
            const query = normalize(nextValue)
            const hasMatches = !query ? false : some(options, option => includes(normalize(option.name), query))
            setOpen(hasMatches)
          }}
          className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-dark-500 focus:border-dark-500 sm:text-sm ${
            isRtl ? 'pl-10' : 'pr-10'
          }`}
          placeholder={placeholder}
          autoComplete="off"
        />
        <button
          type="button"
          className={`absolute inset-y-0 flex items-center ${isRtl ? 'left-0 pl-2' : 'right-0 pr-2'}`}
          onMouseDown={e => {
            e.preventDefault()
            setPreferFullList(true)
            setOpen(prev => !prev)
          }}
          tabIndex={-1}
        >
          <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </button>
      </div>
      {open && listOptions.length > 0 && (
        <ul className="absolute z-[9999] mt-1 max-h-[200px] w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {listOptions.map(option => (
            <li key={option.id}>
              <button
                type="button"
                className="w-full cursor-default select-none px-3 py-2 text-left text-gray-700 hover:bg-dark-600 hover:text-white"
                onMouseDown={e => {
                  e.preventDefault()
                  onChange(option.name, option)
                  setOpen(false)
                  setPreferFullList(false)
                }}
              >
                {option.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const AddCountryModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    country: '',
    countryAr: '',
    active: true,
  })

  const englishOptions = useMemo(
    () =>
      countries.map(country => ({
        id: country.id,
        name: country.name,
        countryAr: country.nameAr || '',
      })),
    []
  )

  const arabicOptions = useMemo(
    () =>
      countries
        .filter(country => country.nameAr)
        .map(country => ({
          id: `${country.id}-ar`,
          name: country.nameAr,
          country: country.name,
        })),
    []
  )

  useEffect(() => {
    if (id) {
      fetchCountryData()
    }
  }, [id])

  const fetchCountryData = async () => {
    try {
      const response = await apiGet(`country/${id}`)
      if (response.success) {
        const data = response.data
        setFormData({
          country: data.country || '',
          countryAr: data.countryAr || '',
          active: data.active !== undefined ? data.active : true,
        })
      } else {
        notify.error(t('addCountryModal.messages.failedToFetchCountryData'))
      }
    } catch (error) {
      console.error('Error fetching country data:', error)
      notify.error(t('addCountryModal.messages.failedToFetchCountryData'))
    }
  }

  const handleEnglishChange = (country, option) => {
    if (option) {
      setFormData(prevData => ({
        ...prevData,
        country: option.name,
        countryAr: option.countryAr || '',
      }))
      return
    }

    const matchedCountry = find(countries, item => normalize(item.name) === normalize(country))
    setFormData(prevData => ({
      ...prevData,
      country,
      ...(matchedCountry ? { countryAr: matchedCountry.nameAr || '' } : {}),
    }))
  }

  const handleArabicChange = (countryAr, option) => {
    if (option) {
      setFormData(prevData => ({
        ...prevData,
        countryAr: option.name,
        country: option.country || '',
      }))
      return
    }

    const matchedCountry = find(countries, item => normalize(item.nameAr) === normalize(countryAr))
    setFormData(prevData => ({
      ...prevData,
      countryAr,
      ...(matchedCountry ? { country: matchedCountry.name } : {}),
    }))
  }

  const handleSwitchChange = checked => {
    setFormData(prevData => ({
      ...prevData,
      active: checked,
    }))
  }

  const handleSubmit = async () => {
    if (!trim(formData.country)) {
      notify.error(t('addCountryModal.messages.countryNameRequired'))
      return
    }
    if (!trim(formData.countryAr)) {
      notify.error(t('addCountryModal.messages.countryArabicNameRequired'))
      return
    }

    const payload = {
      country: formData.country,
      countryAr: formData.countryAr,
      active: formData.active,
    }

    try {
      let response
      if (id) {
        response = await apiPut(`country/${id}`, payload)
        if (response.success) {
          notify.success(t('addCountryModal.messages.countryUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('country', payload)
        if (response.success) {
          notify.success(t('addCountryModal.messages.countryAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) {
          customClose()
        }
        close()
      } else {
        const message = toLower(response.message || '')
        const isDuplicate = includes(message, 'already exists') || includes(message, 'duplicate')
        notify.error(isDuplicate ? t('addCountryModal.messages.countryAlreadyExists') : response.message || t('addCountryModal.messages.operationFailed'))
      }
    } catch (error) {
      console.error('Error saving country:', error)
      notify.error(t('addCountryModal.messages.failedToSaveCountry'))
    }
  }

  const handleCancel = () => {
    if (customClose) {
      customClose()
    }
    close()
  }

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addCountryModal.labels.country')}
        </label>
        <CountryCombobox
          id="country"
          value={formData.country}
          onChange={handleEnglishChange}
          options={englishOptions}
          placeholder={t('addCountryModal.placeholders.selectCountry')}
        />
      </div>
      <div>
        <label htmlFor="countryAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addCountryModal.labels.countryArabic')}
        </label>
        <CountryCombobox
          id="countryAr"
          value={formData.countryAr}
          onChange={handleArabicChange}
          options={arabicOptions}
          placeholder={t('addCountryModal.placeholders.enterCountryArabic')}
          dir="rtl"
        />
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="active" className="block text-sm font-medium text-gray-700">
          {t('addCountryModal.labels.active')}
        </label>
        <Switch value={formData.active} onChange={handleSwitchChange} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addCountryModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addCountryModal.buttons.updateCountry') : t('addCountryModal.buttons.addCountry')}
        </button>
      </div>
    </div>
  )
}

export default AddCountryModal
